import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customerAPI, ordersAPI } from "../../utils/api";
import {
  Package,
  MapPin,
  CheckCircle,
  Clock,
  Truck,
  Navigation,
  AlertCircle,
  Search,
  Loader2,
  Eye,
  XCircle,
  ShoppingBag,
  FileText,
  Star,
  ChevronRight,
  User,
  Phone,
  DollarSign,
  Weight,
  Navigation as LiveTrackIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import InvoiceModal from "../../components/InvoiceModal";

const formatMoney = (val) => {
  const num =
    typeof val === "string"
      ? parseFloat(val)
      : typeof val === "number"
        ? val
        : 0;
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "Invalid Date";
  }
};

const statusConfig = {
  pending: {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    icon: Clock,
    label: "Pending",
    description: "Waiting for assignment",
  },
  assigned: {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: Truck,
    label: "Assigned",
    description: "Courier assigned",
  },
  picked_up: {
    color: "bg-violet-100 text-violet-800 border-violet-200",
    icon: Package,
    label: "Picked Up",
    description: "Package collected",
  },
  in_transit: {
    color: "bg-cyan-100 text-cyan-800 border-cyan-200",
    icon: Navigation,
    label: "In Transit",
    description: "On the way",
  },
  delivered: {
    color: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icon: CheckCircle,
    label: "Delivered",
    description: "Successfully delivered",
  },
  failed: {
    color: "bg-rose-100 text-rose-800 border-rose-200",
    icon: XCircle,
    label: "Failed",
    description: "Delivery failed",
  },
  cancelled: {
    color: "bg-gray-100 text-gray-800 border-gray-200",
    icon: AlertCircle,
    label: "Cancelled",
    description: "Order cancelled",
  },
};

const CustomerOrders = () => {
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);

  const {
    data: ordersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["customerOrders", statusFilter, search],
    queryFn: () =>
      customerAPI.myOrders({
        status: statusFilter || undefined,
        search: search || undefined,
      }),
    retry: 2,
  });

  const orders = ordersData?.data?.data?.data || [];

  const filteredOrders = orders.filter((order) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      order.tracking_number?.toLowerCase().includes(searchLower) ||
      order.delivery_address?.toLowerCase().includes(searchLower) ||
      order.recipient_name?.toLowerCase().includes(searchLower)
    );
  });

  const openInvoice = (order) => {
    setSelectedOrder(order);
    setShowInvoice(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
        <p className="font-medium">Failed to load orders</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-3 btn-secondary text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Orders</h1>
          <p className="text-gray-500 mt-1">
            Track and manage all your deliveries
          </p>
        </div>
        <Link
          to="/orders/new"
          className="btn-primary flex items-center justify-center sm:w-auto"
        >
          <ShoppingBag className="w-4 h-4 mr-2" />
          New Order
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by tracking number, address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {[
              "",
              "pending",
              "assigned",
              "picked_up",
              "in_transit",
              "delivered",
            ].map((status) => {
              const config = status ? statusConfig[status] : null;
              const Icon = config?.icon || Package;
              return (
                <button
                  key={status || "all"}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    statusFilter === status
                      ? "bg-primary-600 text-white shadow-sm"
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 inline mr-1.5" />
                  {status ? config?.label : "All"}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="card text-center py-16">
            <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 font-medium text-lg">No orders found</p>
            <p className="text-sm text-gray-400 mt-1">
              {search
                ? "Try different search terms"
                : "Create your first order to get started"}
            </p>
            {!search && (
              <Link
                to="/orders/new"
                className="mt-4 btn-primary inline-flex items-center"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Create Order
              </Link>
            )}
          </div>
        ) : (
          filteredOrders.map((order) => {
            const config = statusConfig[order.status];
            const StatusIcon = config?.icon || Clock;
            const canLiveTrack = [
              "assigned",
              "picked_up",
              "in_transit",
            ].includes(order.status);

            return (
              <div
                key={order.id}
                className="card hover:shadow-lg transition-all duration-200 border border-gray-100"
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  {/* Left: Status & Info */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div
                        className={`p-2.5 rounded-lg ${config?.color || "bg-gray-100"}`}
                      >
                        <StatusIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-mono text-sm font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                            {order.tracking_number}
                          </span>
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${config?.color || "bg-gray-100"}`}
                          >
                            {config?.label || order.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {config?.description}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                      <div className="flex items-start space-x-2">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">
                            Delivery Address
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {order.delivery_address}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <Package className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">
                            Package
                          </p>
                          <p className="text-sm font-medium text-gray-900">
                            {order.package_type} • {order.weight}kg
                          </p>
                        </div>
                      </div>
                    </div>

                    {order.courier && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Truck className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium text-gray-900">
                            Courier: {order.courier.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {order.courier.phone}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: Amount & Actions */}
                  <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-start gap-4 md:min-w-[140px]">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        EGP {formatMoney(order.total_amount)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      {/* LIVE TRACK BUTTON - NEW */}
                      {canLiveTrack && (
                        <Link
                          to={`/customer/live-track/${order.id}`}
                          className="btn-primary text-sm py-2 px-4 flex items-center bg-cyan-600 hover:bg-cyan-700 border-cyan-600"
                          title="Live Track"
                        >
                          <LiveTrackIcon className="w-4 h-4 mr-1.5" />
                          Live Track
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowDetails(true);
                        }}
                        className="btn-secondary text-sm py-2 px-4 flex items-center"
                      >
                        <Eye className="w-4 h-4 mr-1.5" />
                        Details
                      </button>
                      {/* INVOICE BUTTON */}
                      <button
                        onClick={() => openInvoice(order)}
                        className="btn-secondary text-sm py-2 px-4 flex items-center text-violet-600 hover:bg-violet-50 border-violet-200"
                        title="View Invoice"
                      >
                        <FileText className="w-4 h-4 mr-1.5" />
                        Invoice
                      </button>
                      {order.status === "pending" && (
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                "Are you sure you want to cancel this order?",
                              )
                            ) {
                              customerAPI
                                .cancelOrder(order.id)
                                .then(() => window.location.reload());
                            }
                          }}
                          className="text-sm py-2 px-4 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors flex items-center"
                        >
                          <XCircle className="w-4 h-4 mr-1.5" />
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Progress Bar for Active Orders */}
                {["pending", "assigned", "picked_up", "in_transit"].includes(
                  order.status,
                ) && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                      <span>Order Progress</span>
                      <span>
                        {[
                          "pending",
                          "assigned",
                          "picked_up",
                          "in_transit",
                        ].indexOf(order.status) + 1}{" "}
                        of 4
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                        style={{
                          width: `${((["pending", "assigned", "picked_up", "in_transit"].indexOf(order.status) + 1) / 4) * 100}%`,
                        }}
                      ></div>
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-gray-500">
                      <span
                        className={
                          order.status === "pending"
                            ? "text-primary-600 font-medium"
                            : ""
                        }
                      >
                        Pending
                      </span>
                      <span
                        className={
                          order.status === "assigned"
                            ? "text-primary-600 font-medium"
                            : ""
                        }
                      >
                        Assigned
                      </span>
                      <span
                        className={
                          order.status === "picked_up"
                            ? "text-primary-600 font-medium"
                            : ""
                        }
                      >
                        Picked Up
                      </span>
                      <span
                        className={
                          order.status === "in_transit"
                            ? "text-primary-600 font-medium"
                            : ""
                        }
                      >
                        In Transit
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Details Modal */}
      {showDetails && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Order Details
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">Tracking Number</span>
                <span className="font-mono font-semibold text-gray-900">
                  {selectedOrder.tracking_number}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Status</p>
                  <p className="font-semibold text-gray-900">
                    {statusConfig[selectedOrder.status]?.label ||
                      selectedOrder.status}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Date</p>
                  <p className="font-semibold text-gray-900">
                    {formatDate(selectedOrder.created_at)}
                  </p>
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">
                  Delivery Address
                </p>
                <p className="text-sm font-medium text-gray-900">
                  {selectedOrder.delivery_address}
                </p>
              </div>
              <div className="border-t border-gray-100 pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">
                    EGP {formatMoney(selectedOrder.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-gray-500">Delivery Fee</span>
                  <span className="font-medium">
                    EGP {formatMoney(selectedOrder.delivery_fee)}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-gray-500">Tax</span>
                  <span className="font-medium">
                    EGP {formatMoney(selectedOrder.tax)}
                  </span>
                </div>
                <div className="flex justify-between text-base font-bold mt-3 pt-3 border-t border-gray-100">
                  <span>Total</span>
                  <span className="text-primary-600">
                    EGP {formatMoney(selectedOrder.total_amount)}
                  </span>
                </div>
              </div>
              <div className="flex gap-3">
                {/* Live Track button in modal too */}
                {["assigned", "picked_up", "in_transit"].includes(
                  selectedOrder.status,
                ) && (
                  <Link
                    to={`/customer/live-track/${selectedOrder.id}`}
                    onClick={() => setShowDetails(false)}
                    className="flex-1 btn-primary py-2.5 flex items-center justify-center bg-cyan-600 hover:bg-cyan-700"
                  >
                    <LiveTrackIcon className="w-4 h-4 mr-2" /> Live Track
                  </Link>
                )}
                <button
                  onClick={() => {
                    setShowDetails(false);
                    openInvoice(selectedOrder);
                  }}
                  className="flex-1 btn-primary py-2.5 flex items-center justify-center"
                >
                  <FileText className="w-4 h-4 mr-2" /> View Invoice
                </button>
              </div>
              {selectedOrder.status === "delivered" && (
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    Rate your delivery
                  </p>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        className="p-1 hover:scale-110 transition-transform"
                      >
                        <Star className="w-8 h-8 text-gray-300 hover:text-amber-400 fill-current" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* INVOICE MODAL */}
      {showInvoice && selectedOrder && (
        <InvoiceModal
          order={selectedOrder}
          onClose={() => {
            setShowInvoice(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
};

export default CustomerOrders;
